// The public Listen player: plays the song the street is playing, at the same
// moment as the lights, on any phone (car Bluetooth or CarPlay included).
//
// The hub says which song is playing and when it started (/api/live). The
// phone estimates the hub's clock from those replies and starts the song
// muted, jumping until it's at the right position, then turns the sound on. It
// stays in step by nudging playback speed (or, on iPhones, where speed changes
// cut the sound, by jumping only if it drifts far). An <audio> element is used rather than Web Audio so it keeps playing
// with the screen locked and isn't silenced by an iPhone's mute switch.
import { $ } from "./site.ts";

interface Live {
  street: string;
  showHours: string | null;
  serverTime: number;
  nowPlaying: {
    songId: string;
    title: string;
    startEpochMs: number;
    duration: number;
    listenable: boolean;
    audioUrl: string | null;
  } | null;
}

const OFFSET_KEY = "cc-listen-offset";
const POLL_MS = 3000;
// A hard jump cuts the sound, so at most one per this long; speed nudges fix the rest.
const MIN_JUMP_GAP_MS = 4000;
// Without speed nudges (iPhone): small jumps line the song up while it's muted
// (to within REFINE_SECONDS). Turning the sound on can cost a moment, so one
// more jump is allowed after if that put it AUDIBLE_REFINE_SECONDS off; then
// it's left alone unless it drifts NO_NUDGE_DRIFT_SECONDS.
const REFINE_SECONDS = 0.06;
const AUDIBLE_REFINE_SECONDS = 0.1;
const MAX_REFINES = 1;
const NO_NUDGE_DRIFT_SECONDS = 0.35;
// Each song starts muted while it loads and lines up (its jumps would be heard
// as gaps), then the sound comes on. Never longer than this.
const WARM_UP_MAX_MS = 6000;
// Replies about the hub's clock to have before the sound comes on. One slow
// reply can be off by a tenth of a second, which would mean a jump later.
const CLOCK_REPLIES = 5;
// How long after a jump the audio is first judged.
const LANDING_MS = 700;
// A phone left on in a pocket would download every song all night, and the
// hub pays for that data. So after this many songs or this long without a tap,
// the music stops before the next song downloads and asks "Still listening?".
const STILL_LISTENING_SONGS = 8;
const STILL_LISTENING_MS = 30 * 60 * 1000;

// iPhones and iPads cut the sound for a moment on every playback-speed change,
// so there the song is kept in step by jumps alone. ?nudge=0 or 1 overrides.
export function speedNudgesWork(): boolean {
  const forced = new URLSearchParams(location.search).get("nudge");
  if (forced !== null) return forced !== "0";
  const ua = navigator.userAgent;
  return !(/iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1));
}

export interface ListenOptions {
  // The admin's speaker player: plays every song (public or not) from the
  // admin-only audio route, and keeps the screen awake while it plays.
  speakers?: boolean;
}

export function setupListen(root: HTMLElement, options: ListenOptions = {}): void {
  const button = $<HTMLButtonElement>("[data-listen]", root);
  const title = $("[data-live-title]", root);
  const status = $("[data-live-status]", root);
  const hours = $("[data-live-hours]", root);
  const slider = $<HTMLInputElement>("[data-live-offset]", root);
  const sliderValue = $("[data-live-offset-value]", root);

  const audio = new Audio();
  audio.preload = "auto";
  const nudges = speedNudgesWork();
  const debug = setupDebug(root, audio, () => {
    const pos = position();
    return `pos ${pos?.toFixed(2) ?? "-"} drift ${pos === null ? "-" : (audio.currentTime - pos).toFixed(3)} lead ${jumpLead.toFixed(2)}${audio.muted ? " muted" : ""}`;
  });
  let live: Live | null = null;
  let listening = false;
  let loadedSong: string | null = null;
  // Hub clock minus this phone's clock, from the reply with the fastest round trip.
  let clockOffset = 0;
  let bestRtt = Infinity;
  let clockReplies = 0;
  let userOffsetMs = 0;
  // Since the listener last tapped: songs downloaded, and when. (Not for the
  // admin's speaker player, which has to play all night.)
  let songsSinceTap = 0;
  let lastTapAt = Date.now();
  let askedStillListening = false;
  try {
    userOffsetMs = Number(localStorage.getItem(OFFSET_KEY)) || 0;
  } catch {
    // Storage blocked: the offset just won't be remembered.
  }
  slider.value = String(userOffsetMs);
  sliderValue.textContent = describeOffset(userOffsetMs);

  function describeOffset(ms: number): string {
    if (Math.abs(ms) < 10) return "in step";
    return ms > 0 ? `music ${ms} ms earlier` : `music ${-ms} ms later`;
  }

  // Where this player gets a song's audio, if it may play it.
  function audioUrl(song: NonNullable<Live["nowPlaying"]>): string | null {
    if (options.speakers) return `/api/songs/${song.songId}/audio`;
    return song.listenable ? song.audioUrl : null;
  }
  const playable = (song: Live["nowPlaying"]) => Boolean(song && audioUrl(song));

  // Seconds into the song the street is at right now (including the
  // listener's own adjustment for Bluetooth delay).
  function position(): number | null {
    const song = live?.nowPlaying;
    if (!song) return null;
    return (Date.now() + clockOffset - song.startEpochMs + userOffsetMs) / 1000;
  }

  async function poll(): Promise<void> {
    const t0 = Date.now();
    try {
      const res = await fetch("/api/live", { cache: "no-store" });
      const t1 = Date.now();
      const data = (await res.json()) as Live;
      const rtt = t1 - t0;
      // A slow reply tells us less about the clock; keep the best, but let it
      // age so a phone that changes networks re-learns.
      clockReplies++;
      if (rtt < bestRtt) {
        bestRtt = rtt;
        clockOffset = data.serverTime - (t0 + t1) / 2;
      } else {
        bestRtt += 5;
      }
      live = data;
    } catch {
      // Offline for a moment; keep the last known state.
    }
    render();
    if (listening) sync();
  }

  function render(): void {
    const song = live?.nowPlaying;
    const pos = position();
    const idle = !song || pos === null || pos > song.duration + 2;
    // "The show plays between 5:00pm and 10:00pm", once the admin sets a schedule.
    hours.textContent = live?.showHours ?? "";
    hours.hidden = !idle || !live?.showHours;
    // A spinner while the song loads and lines up, so it's clear it's working.
    status.classList.toggle("loading", listening && warming && !idle && playable(song));
    if (askedStillListening && !listening) {
      title.textContent = "Still listening?";
      status.textContent = "We paused the music in case this phone was left on. Tap below to keep listening.";
    } else if (idle) {
      title.textContent = "The show isn't playing right now";
      status.textContent = "Come back during the evening show. The lights may still be on!";
    } else if (!playable(song)) {
      title.textContent = song.title;
      status.textContent = "This song is for the street only. Keep watching the lights; the next one may play here.";
    } else {
      title.textContent = song.title;
      status.textContent = listening
        ? warming ? "Lining up with the lights…" : "Playing in step with the lights."
        : "Tap Listen to hear it in step with the lights.";
    }
    button.textContent = options.speakers
      ? listening ? "Stop playing here" : "▶ Play the show here"
      : listening ? "Stop listening" : askedStillListening ? "▶ Yes, keep listening" : "▶ Listen";
    button.setAttribute("aria-pressed", String(listening));
    if ("mediaSession" in navigator && playable(song ?? null)) {
      navigator.mediaSession.metadata = new MediaMetadata({ title: song!.title, artist: live?.street ?? "" });
    }
  }

  // A jump to a new spot isn't heard until the phone has fetched and decoded
  // audio there, which on a phone can take longer than the drift we allow. So
  // each jump aims ahead, by however far the last one landed off; otherwise the
  // song lands late, jumps again, lands late again, and cuts out every second.
  let jumpLead = 0.3;
  let lastJumpAt = -Infinity;
  // A reading once the audio has been moving a while shows how far off the last
  // jump landed. (Right after a jump the position is the target even though the
  // sound hasn't restarted yet, so earlier readings look better than they are.)
  let checkLanding = false;
  // When the audio last (re)started moving: a jump, or the end of a stall.
  let resumedAt = 0;
  // Whether this song has been moved to the street's position yet.
  let positioned = false;
  // Small lining-up jumps since the last big one (without speed nudges).
  let refines = 0;
  // Muted while the song loads and lines up.
  let warming = false;
  let warmUpStartedAt = 0;

  function warmUp(): void {
    warming = true;
    warmUpStartedAt = performance.now();
    refines = 0;
    audio.muted = true;
  }

  // Sound on once the song is in step (or has had long enough to get there).
  function soundOnWhenReady(drift: number): void {
    if (!warming) return;
    const clockKnown = clockReplies >= CLOCK_REPLIES;
    const inStep = clockKnown && !checkLanding && Math.abs(drift) <= (nudges ? 0.08 : REFINE_SECONDS);
    const tookTooLong = performance.now() - warmUpStartedAt > WARM_UP_MAX_MS;
    if (!inStep && !tookTooLong) return;
    warming = false;
    refines = 0;
    audio.muted = false;
    debug?.(`sound on (${inStep ? "in step" : "took too long"})`);
    render();
  }

  function jump(why: string): void {
    const pos = position();
    if (pos === null) return;
    lastJumpAt = performance.now();
    checkLanding = true;
    debug?.(`jump (${why}) to ${(pos + jumpLead).toFixed(2)}`);
    audio.currentTime = Math.max(0, pos + jumpLead);
  }

  function setRate(rate: number): void {
    if (audio.playbackRate === rate) return;
    debug?.(`speed ${rate.toFixed(3)}`);
    audio.playbackRate = rate;
  }

  // Keep the audio at the street's position.
  function sync(): void {
    const song = live?.nowPlaying;
    const pos = position();
    const url = song ? audioUrl(song) : null;
    if (!song || !url || pos === null || pos > song.duration) {
      if (!audio.paused) audio.pause();
      return;
    }
    if (loadedSong !== song.songId) {
      if (!options.speakers && (songsSinceTap >= STILL_LISTENING_SONGS || Date.now() - lastTapAt >= STILL_LISTENING_MS)) {
        debug?.(`asking "still listening?" after ${songsSinceTap} songs`);
        askedStillListening = true;
        stop();
        return;
      }
      songsSinceTap++;
      loadedSong = song.songId;
      positioned = false;
      checkLanding = false;
      warmUp();
      debug?.(`load ${url}`);
      audio.src = url;
      setRate(1);
    }
    // Can't move until the song's length is known; "loadedmetadata" calls back.
    if (audio.readyState < HTMLMediaElement.HAVE_METADATA) return;
    if (pos < 0) {
      // Song starts in a moment.
      if (!audio.paused) audio.pause();
      if (audio.currentTime !== 0) audio.currentTime = 0;
      positioned = true;
      setTimeout(() => listening && sync(), -pos * 1000);
      return;
    }
    if (audio.paused || !positioned) {
      positioned = true;
      jump(audio.paused ? "start" : "new song");
      if (audio.paused) {
        void audio.play().catch(() => {
          listening = false;
          render();
        });
      }
      return;
    }
    // Mid-jump or waiting for audio: the position isn't moving, so it can't be judged.
    if (audio.seeking || audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;
    const drift = audio.currentTime - pos; // positive = audio ahead
    const sinceJump = performance.now() - lastJumpAt;
    // Not judged until the audio has been heard for a moment.
    if (performance.now() - Math.max(lastJumpAt, resumedAt) < LANDING_MS) return;
    if (checkLanding) {
      checkLanding = false;
      jumpLead = Math.min(3, Math.max(0, jumpLead - drift));
      debug?.(`landed off by ${drift.toFixed(3)}s`);
    }
    if (!nudges) {
      // A phone's audio keeps time well once lined up; only the start needs care.
      if (Math.abs(drift) > NO_NUDGE_DRIFT_SECONDS && sinceJump > MIN_JUMP_GAP_MS) {
        refines = 0;
        jump(`off ${drift.toFixed(2)}s`);
      } else if (
        Math.abs(drift) > (warming ? REFINE_SECONDS : AUDIBLE_REFINE_SECONDS) &&
        (warming || refines < MAX_REFINES) &&
        !checkLanding &&
        clockReplies >= CLOCK_REPLIES
      ) {
        refines++;
        jump(`line up, off ${drift.toFixed(3)}s`);
      } else {
        soundOnWhenReady(drift);
      }
      return;
    }
    if (Math.abs(drift) > 0.25 && sinceJump > MIN_JUMP_GAP_MS) {
      setRate(1);
      jump(`off ${drift.toFixed(2)}s`);
      return;
    }
    // Catch up or ease back gently (at most 5%, not audible as pitch). Small
    // changes are skipped: every change can blip the sound on some phones.
    const rate = Math.abs(drift) > 0.03 ? 1 - Math.max(-0.05, Math.min(0.05, drift * 0.5)) : 1;
    if (Math.abs(rate - audio.playbackRate) > 0.01 || rate === 1) setRate(rate);
    soundOnWhenReady(drift);
  }

  // Any tap (or Play on the lock screen) shows someone's still there.
  function tapped(): void {
    songsSinceTap = 0;
    lastTapAt = Date.now();
  }

  function start(): void {
    tapped();
    askedStillListening = false;
    listening = true;
    // iPhone: play through the mute switch, like a music app.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "playback";
    warmUp();
    // Learn the hub's clock well before the sound comes on.
    clockReplies = Math.min(clockReplies, 1);
    void (async () => {
      for (let i = 0; i < CLOCK_REPLIES && listening; i++) await poll();
    })();
    // Start inside the tap so phones allow sound (muted until it's in step).
    sync();
    if (audio.paused && playable(live?.nowPlaying ?? null)) void audio.play().catch(() => {});
    if (options.speakers) void keepAwake(true);
    render();
  }

  function stop(): void {
    listening = false;
    audio.pause();
    if (options.speakers) void keepAwake(false);
    render();
  }

  // A sleeping computer would stop the street's music.
  let wakeLock: { release(): Promise<void> } | null = null;
  async function keepAwake(on: boolean): Promise<void> {
    const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } };
    if (on && !wakeLock && nav.wakeLock && document.visibilityState === "visible") {
      wakeLock = await nav.wakeLock.request("screen").catch(() => null);
    } else if (!on && wakeLock) {
      await wakeLock.release().catch(() => {});
      wakeLock = null;
    }
  }
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && listening && options.speakers) {
      wakeLock = null;
      void keepAwake(true);
    }
  });

  button.addEventListener("click", () => (listening ? stop() : start()));
  document.addEventListener("pointerdown", tapped);
  document.addEventListener("keydown", tapped);
  slider.addEventListener("input", () => {
    userOffsetMs = Number(slider.value);
    sliderValue.textContent = describeOffset(userOffsetMs);
    try {
      localStorage.setItem(OFFSET_KEY, String(userOffsetMs));
    } catch {
      // Not remembered; still applied now.
    }
    if (listening) sync();
  });
  if ("mediaSession" in navigator) {
    navigator.mediaSession.setActionHandler("play", start);
    navigator.mediaSession.setActionHandler("pause", stop);
  }
  audio.addEventListener("ended", () => listening && sync());
  audio.addEventListener("loadedmetadata", () => listening && sync());
  // Loading and jumping take a moment; correct as soon as sound actually starts.
  for (const event of ["seeked", "playing"]) {
    audio.addEventListener(event, () => {
      resumedAt = performance.now();
      if (listening) sync();
    });
  }

  debug?.(`${navigator.userAgent} | speed nudges ${nudges ? "on" : "off"}`);
  void poll();
  setInterval(poll, POLL_MS);
  // Between polls, keep correcting drift.
  setInterval(() => listening && sync(), 1000);
}

// /listen?debug: a log of what the player does, on screen, for finding out
// why a particular phone sounds choppy. "Copy" puts it on the clipboard.
function setupDebug(root: HTMLElement, audio: HTMLAudioElement, state: () => string): ((line: string) => void) | null {
  if (!new URLSearchParams(location.search).has("debug")) return null;
  const box = document.createElement("div");
  box.style.cssText = "text-align:left;margin-top:1rem";
  const copy = document.createElement("button");
  copy.type = "button";
  copy.textContent = "Copy log";
  const pre = document.createElement("pre");
  pre.style.cssText = "font-size:11px;white-space:pre-wrap;max-height:50vh;overflow:auto";
  box.append(copy, pre);
  root.append(box);
  const lines: string[] = [];
  const t0 = performance.now();
  const log = (line: string) => {
    const t = ((performance.now() - t0) / 1000).toFixed(1);
    const buffered = audio.buffered.length ? audio.buffered.end(audio.buffered.length - 1).toFixed(1) : "-";
    lines.push(`${t} ${line} | at ${audio.currentTime.toFixed(2)} ${state()} speed ${audio.playbackRate} ready ${audio.readyState} buf ${buffered}`);
    if (lines.length > 300) lines.shift();
    pre.textContent = lines.slice(-40).join("\n");
  };
  for (const event of ["waiting", "stalled", "playing", "seeked", "pause", "ended", "error"]) {
    audio.addEventListener(event, () => log(event));
  }
  // A once-a-second heartbeat shows drift between events.
  setInterval(() => !audio.paused && log("tick"), 1000);
  copy.addEventListener("click", () => {
    void navigator.clipboard.writeText(lines.join("\n")).then(() => (copy.textContent = "Copied"));
  });
  return log;
}
